import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  externalApiTokensAPI,
  type AvailableScope,
  type ExternalApiTokenWithPlaintext,
} from '../../lib/api/externalApiTokens';

type ExpiryChoice = '90' | '180' | '365' | 'never';

interface CreateTokenModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the newly minted plaintext token after a successful create. */
  onCreated: (token: ExternalApiTokenWithPlaintext) => void;
  /** CR-6543 r4: the customer company the token is minted FOR (staff surface). */
  companyId?: number;
}

/**
 * Dialog for creating a new external API token.
 *
 * On success, hands the plaintext token (one-time-readable) to the parent via
 * `onCreated`, which is responsible for showing the reveal modal.
 */
export function CreateTokenModal({ open, onOpenChange, onCreated, companyId }: CreateTokenModalProps) {
  const [label, setLabel] = useState('');
  const [expiry, setExpiry] = useState<ExpiryChoice>('365');
  const [submitting, setSubmitting] = useState(false);
  const [labelError, setLabelError] = useState<string | null>(null);
  // CR-6543 r4: module scopes — offered list from the backend registry.
  const [availableScopes, setAvailableScopes] = useState<AvailableScope[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);

  // Reset form whenever the modal opens
  useEffect(() => {
    if (open) {
      setLabel('');
      setExpiry('365');
      setLabelError(null);
      setSubmitting(false);
      externalApiTokensAPI.availableScopes()
        .then((r) => {
          setAvailableScopes(r.data.scopes);
          setSelectedScopes(r.data.default);
        })
        .catch((err: unknown) => {
          console.error('Failed to load available scopes:', err);
          toast.error('Failed to load available module scopes');
        });
    }
  }, [open]);

  const toggleScope = (key: string) => {
    setSelectedScopes((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key],
    );
  };

  const validate = (): boolean => {
    const trimmed = label.trim();
    if (trimmed.length < 2) {
      setLabelError('Label must be at least 2 characters.');
      return false;
    }
    if (trimmed.length > 80) {
      setLabelError('Label must be 80 characters or fewer.');
      return false;
    }
    if (selectedScopes.length === 0) {
      toast.error('Select at least one module scope.');
      return false;
    }
    setLabelError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const expiresInDays: number | null =
      expiry === 'never' ? null : Number.parseInt(expiry, 10);

    setSubmitting(true);
    try {
      const response = await externalApiTokensAPI.create(label.trim(), expiresInDays, selectedScopes, companyId);
      onCreated(response.data);
      onOpenChange(false);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to create API token.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create API Token</DialogTitle>
          <DialogDescription>
            Generate a long-lived token for external integrations such as Power BI.
            You will only see the plaintext token once — copy it immediately.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="token-label">Label</Label>
            <Input
              id="token-label"
              type="text"
              placeholder="e.g. Power BI — Quality Reports"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={80}
              autoFocus
              disabled={submitting}
            />
            {labelError && (
              <p className="text-sm text-destructive">{labelError}</p>
            )}
            <p className="text-xs text-muted-foreground">
              A short, descriptive name so you can identify this token later.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Module access</Label>
            <div className="space-y-1">
              {availableScopes.map((scope) => (
                <label key={scope.key} className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={selectedScopes.includes(scope.key)}
                    onChange={() => toggleScope(scope.key)}
                    disabled={submitting}
                  />
                  <span>
                    <span className="font-medium capitalize">{scope.key}</span>
                    <span className="block text-xs text-muted-foreground">{scope.label}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              The key only works against the selected modules&apos; endpoints.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Expiry</Label>
            <RadioGroup
              value={expiry}
              onValueChange={(value: string) => setExpiry(value as ExpiryChoice)}
              disabled={submitting}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="90" id="expiry-90" />
                <Label htmlFor="expiry-90" className="font-normal cursor-pointer">
                  90 days
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="180" id="expiry-180" />
                <Label htmlFor="expiry-180" className="font-normal cursor-pointer">
                  180 days
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="365" id="expiry-365" />
                <Label htmlFor="expiry-365" className="font-normal cursor-pointer">
                  365 days
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="never" id="expiry-never" />
                <Label htmlFor="expiry-never" className="font-normal cursor-pointer">
                  Never (not recommended)
                </Label>
              </div>
            </RadioGroup>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                'Create Token'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
